<template>
  <div v-if="supplyOrder">
    <base-page-heading
      :title="title"
      show-back-button
    >
      <template #secondary-title>
        <status-badge
          :status="supplyOrder.status"
          :status-variants="statusVariants"
          :sub-status="supplyOrder.backordered ? 'backordered' : null"
        />
      </template>
      <template #extra>
        <b-button
          v-if="$can('confirm', supplyOrder)"
          :key="`${supplyOrderKey}-confirm-button`"
          v-b-tooltip.hover
          variant="primary"
          class="d-none d-md-inline"
          title="Confirm the Supply Order"
          v-b-modal.supply-order-confirm-modal
        >
          <i class="fa fa-check mr-1" />
          Confirm
        </b-button>
        <b-button
          v-if="$can('invoice', supplyOrder)"
          :key="`${supplyOrderKey}-invoice-button`"
          variant="primary"
          v-b-modal.supply-order-invoice-modal
          class="d-none d-md-inline"
        >
          <i class="fa fa-check mr-1" />
          Invoice
        </b-button>
        <b-button
          v-if="$can('update', 'SupplyOrder')"
          variant="primary"
          v-b-modal.supply-order-edit-modal
          class="d-none d-md-inline"
        >
          <i class="fa fa-pencil-alt mr-1" /> Edit
        </b-button>

        <sfs-download-button
          :filename="`${supplyOrder.number}.pdf`"
          label="Summary"
          :provider-params="{ id: supplyOrder.id }"
          :file-provider="$supplyOrdersAPI.getPDF"
          v-b-tooltip.hover
          title="Download the Summary PDF"
          class="d-none d-md-inline"
        />

        <sfs-download-button
          :filename="`${supplyOrder.number}.pdf`"
          :provider-params="{ id: supplyOrder.id }"
          :file-provider="$supplyOrdersAPI.getPDF"
          v-b-tooltip.hover
          title="Download the Summary PDF"
          class="d-md-none"
        />

        <b-button
          variant="outline-secondary"
          v-b-modal.purchase-order-email-modal
          v-b-tooltip.hover
          title="Email the Purchase Order"
          class="d-none d-md-inline"
        >
          <i class="fa fa-envelope" />
        </b-button>

        <b-dropdown
          variant="outline-secondary"
          no-caret
          right
        >
          <template #button-content>
            <i class="fa fa-ellipsis-v" />
          </template>

          <div class="d-md-none">
            <b-dropdown-item
              v-if="$can('confirm', supplyOrder)"
              :key="`${supplyOrderKey}-confirm-button`"
              v-b-modal.supply-order-confirm-modal
            >
              <i class="fa fa-check mr-1" /> Confirm
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('invoice', supplyOrder)"
              :key="`${supplyOrderKey}-invoice-button`"
              v-b-modal.supply-order-invoice-modal
            >
              <i class="fa fa-check mr-1" /> Invoice
            </b-dropdown-item>

            <b-dropdown-item
              v-if="$can('update', 'SupplyOrder')"
              v-b-modal.supply-order-edit-modal
            >
              <i class="fa fa-pencil-alt mr-1" /> Edit
            </b-dropdown-item>

            <b-dropdown-item
              v-b-modal.purchase-order-email-modal
            >
              <i class="fa fa-envelope mr-1" /> Email
            </b-dropdown-item>

            <b-dropdown-divider />
          </div>

          <b-dropdown-item
            v-if="$can('update', 'SupplyOrder')"
            @click="toggleActive"
          >
            <i
              v-if="!supplyOrder.deleted_at"
              class="fa fa-trash mr-1"
            />
            <i
              v-else
              class="fa fa-undo mr-1"
            />
            {{ toggleActiveTooltipText }}
          </b-dropdown-item>

          <b-dropdown-item
            v-if="$can('read', 'Version')"
            :to="{ name: 'versions', query: { model_id: supplyOrder.id, model: 'SupplyOrder' } }"
          >
            <i class="fa fa-glasses mr-1" />
            Audit Log
          </b-dropdown-item>
        </b-dropdown>
      </template>
    </base-page-heading>
    <div class="content pt-lg-5">
      <b-row
        v-if="location.supply_order_internal_notes"
        class="mb-3"
      >
        <b-col>
          <b-alert
            show
            variant="warning"
            class="mb-0"
          >
            <h5 class="mb-0 alert-heading">
              Note:
            </h5>
            <div class="text-prewrap">
              {{ location.supply_order_internal_notes }}
            </div>
          </b-alert>
        </b-col>
      </b-row>
      <b-row class="mb-3">
        <b-col md="6">
          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  {{ clientTranslation }}
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :to="{ name: 'client', params: { id: client.id } }">
                    {{ client.name }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Location
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link
                    :to="{ name: 'location', params: { id: supplyOrder.location_id } }"
                    v-b-tooltip.hover.window
                    :title="`${supplyOrder.location.street}, ${supplyOrder.location.city}, ${supplyOrder.location.state}, ${supplyOrder.location.zip_code}`"
                  >
                    {{ supplyOrder.location.store_number }}
                    <span v-if="supplyOrder.location.name && supplyOrder.location.name != supplyOrder.location.store_number">- {{ supplyOrder.location.name }}</span>
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="12">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span>{{ supplyOrder.location.street }},</span>
                  <span>
                    {{
                      {
                        city: supplyOrder.location.city,
                        state: supplyOrder.location.state,
                        zip_code: supplyOrder.location.zip_code,
                      } | locationFormat
                    }}
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Billable Total
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <money-format
                    :value="supplyOrder.price.amount"
                    :currency-code="supplyOrder.price.currency"
                    subunits-value
                  />
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Margin
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <money-format
                    :value="margin"
                    :currency-code="supplyOrder.price.currency"
                    subunits-value
                    class="d-inline-block mr-1"
                  />
                  <span>({{ marginPercentage }})</span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="supplyOrder.delivery_notes"
              cols="12"
              sm="6"
            >
              <b-row class="mb-3">
                <b-col>
                  Delivery Notes / Special Instructions
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="supplyOrder.delivery_notes"
              cols="12"
              sm="6"
            >
              <b-row class="mb-3">
                <b-col>
                  <em>{{ supplyOrder.delivery_notes }}</em>
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr class="mt-0 mb-3">

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Invoices
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col v-if="invoices.length > 0">
                  <div
                    v-for="(invoice, index) in invoices"
                    :key="index"
                  >
                    <b-link :to="{ name: 'invoice', params: { id: invoice.id } }">
                      {{ invoice.number }}
                    </b-link>
                  </div>
                </b-col>
                <b-col v-else>
                  -
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>

        <b-col md="6">
          <hr class="mt-0 mb-3 d-md-none">
          <b-row class="mb-3">
            <b-col
              cols="auto"
              class="mr-auto"
            >
              <h4>
                Vendor
              </h4>
            </b-col>
            <b-col class="text-right">
              <sfs-download-button
                :filename="`Purchase Order ${supplyOrder.number}.pdf`"
                label="Purchase Order"
                :provider-params="{ id: supplyOrder.id }"
                :file-provider="$supplyOrdersAPI.getPurchaseOrderPDF"
                v-b-tooltip.hover
                title="Download the Purchase Order"
              />
            </b-col>
          </b-row>

          <b-row>
            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Name
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <b-link :to="{ name: 'vendor', params: { id: vendor.id } }">
                    {{ vendor.name }}
                  </b-link>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Date
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ supplyOrder.date | dateFormat }}
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="12">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Address
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  <span>{{ vendor.address }},</span>
                  <span>
                    {{
                      {
                        city: vendor.city,
                        state: vendor.state,
                        zip_code: vendor.zip_code,
                      } | locationFormat
                    }}
                  </span>
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="supplyOrder.confirmed_at"
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Confirmed At
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ supplyOrder.confirmed_at | dateTimeFormat }}
                </b-col>
              </b-row>
            </b-col>

            <b-col
              v-if="supplyOrder.vendor_number"
              cols="12"
              sm="6"
            >
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Vendor Invoice Number
                </b-col>
              </b-row>

              <b-row class="mb-3">
                <b-col>
                  {{ supplyOrder.vendor_number }}
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Misc Fee
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row>
                <b-col>
                  <money-format
                    :value="vendor.additional_fee.amount"
                    :currency-code="vendor.additional_fee.currency"
                    subunits-value
                    class="text-right text-sm-left"
                  />
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Supplies total
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row>
                <b-col>
                  <money-format
                    :value="supplyOrder.cost.amount - vendor.additional_fee.amount"
                    :currency-code="supplyOrder.cost.currency"
                    subunits-value
                    class="text-right text-sm-left"
                  />
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Tax
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row>
                <b-col>
                  <money-format
                    :value="supplyOrder.cost_tax.amount"
                    :currency-code="supplyOrder.cost_tax.currency"
                    subunits-value
                    class="text-right text-sm-left"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>

          <hr class="my-2">

          <b-row>
            <b-col cols="6">
              <b-row class="text-muted font-w500 font-size-sm mb-1">
                <b-col>
                  Vendor Total
                </b-col>
              </b-row>
            </b-col>

            <b-col cols="6">
              <b-row class="mb-3">
                <b-col>
                  <money-format
                    :value="supplyOrder.cost_tax.amount + supplyOrder.cost.amount"
                    :currency-code="supplyOrder.cost_tax.currency"
                    subunits-value
                    class="text-right text-sm-left"
                  />
                </b-col>
              </b-row>
            </b-col>
          </b-row>
        </b-col>
      </b-row>

      <b-row>
        <b-col lg="12">
          <b-tabs
            v-model="activeTab"
            content-class="pt-4 mb-3"
          >
            <template #tabs-end>
              <div
                v-show="activeTab === 1"
                class="ml-auto"
              >
                <b-button
                  v-if="$can('create', 'Document')"
                  v-b-modal.new-document-modal
                  variant="outline-secondary"
                >
                  <i class="fa fa-plus" />
                </b-button>
              </div>
            </template>
            <b-tab title="Items">
              <supply-order-items-list-filter
                :supply-order="supplyOrder"
                :initial-filters="filters"
                @filters-applied="applyFilters"
                @data-changed="refreshTable"
              />
              <supply-order-items-table
                :columns="supplyOrderItemsColumns"
                :supply-order-id="$route.params.id.toString()"
                :append-to-query="false"
                :filters="filters"
                :key="tableKey"
              />
            </b-tab>

            <b-tab title="Documents">
              <documents-table
                :columns="documentsColumns"
                :append-to-query="false"
                :show-per-page="false"
                :key="documentsKey"
                :list-provider="$documentsAPI.getSupplyOrderDocuments"
                :reference-id="supplyOrder.id"
              />
            </b-tab>
          </b-tabs>
        </b-col>
      </b-row>
    </div>

    <!-- Modals -->
    <supply-orders-edit-modal
      v-if="$can('update', 'SupplyOrder')"
      :key="`${tableKey}-edit-modal`"
      v-model="supplyOrder"
    />
    <purchase-order-email-modal
      v-model="supplyOrder"
    />
    <confirm-modal
      v-if="$can('confirm', supplyOrder)"
      :value="supplyOrder"
      @input="updateOrder"
    />
    <supply-orders-invoice-modal
      v-if="$can('invoice', supplyOrder)"
      :supply-order="supplyOrder"
      :number="supplyOrder.number"
      :date="invoiceDefaults.date"
      :due_date="invoiceDefaults.dueDate"
      :delivery_fee="{ amount: $store.getters.isCMSOrganization ? 1500 : 0 }"
      :invoice_format_id="$store.getters.currentOrganization.default_supply_order_invoice_format_id"
      :invoice_format="$store.getters.currentOrganization.default_supply_order_invoice_format"
    />
    <new-document-modal
      v-if="$can('update', 'SupplyOrder')"
      :reference-id="supplyOrder.id"
      :create-provider="$documentsAPI.createSupplyOrderDocument"
      @on-save="refreshDocuments()"
      internal
      :key="documentsKey"
    />
  </div>
</template>

<script>
import { DateTime } from 'luxon';
import StatusBadge from '@/components/shared/StatusBadge.vue';
import SupplyOrdersEditModal from '@/components/admin/supplyOrders/EditModal.vue';
import SupplyOrdersInvoiceModal from '@/components/admin/supplyOrders/InvoiceModal.vue';
import SupplyOrderItemsTable from '@/components/admin/supplyOrderItems/Table.vue';
import SupplyOrderItemsListFilter from '@/components/admin/supplyOrderItems/ListFilter.vue';
import NewDocumentModal from '@/components/admin/documents/NewModal.vue';
import DocumentsTable from '@/components/admin/documents/Table.vue';
import PurchaseOrderEmailModal from '@/components/admin/supplyOrders/PurchaseOrderEmailModal.vue';
import ConfirmModal from '@/components/admin/supplyOrders/ConfirmModal.vue';
import { SUPPLY_ORDER_ITEM_TABLE_NAME } from '@/constants/supplyOrderItems';
import { SUPPLY_ORDER_STATUS_VARIANTS } from '@/constants/supplyOrders';
import { DOCUMENTS_TABLE_NAME } from '@/constants/documents';
import translationMixin from '@/mixins/translationMixin';

export default {
  name: 'SupplyOrdersShow',
  mixins: [translationMixin],
  components: {
    StatusBadge,
    SupplyOrdersEditModal,
    SupplyOrdersInvoiceModal,
    SupplyOrderItemsTable,
    SupplyOrderItemsListFilter,
    NewDocumentModal,
    DocumentsTable,
    PurchaseOrderEmailModal,
    ConfirmModal,
  },
  data() {
    return {
      supplyOrder: null,
      activeTab: 0,
      supplyOrderItemsTable: SUPPLY_ORDER_ITEM_TABLE_NAME,
      documentsTableName: DOCUMENTS_TABLE_NAME,
      invoices: [],
      tableKey: Date.now(),
      supplyOrderKey: `${Date.now()}`,
      vendorEmail: null,
      documentsKey: `documents-${Date.now()}`,
      filters: {
        text: this.text,
      },
      statusVariants: SUPPLY_ORDER_STATUS_VARIANTS,
    };
  },
  mounted() {
    this.getSupplyOrder();
    if (this.$can('read', 'Invoice')) {
      this.getInvoices();
    }
  },
  computed: {
    title() {
      return `Supply Order ${this.supplyOrder.number}`;
    },
    invoiceDefaults() {
      return {
        date: DateTime.now().toISODate(),
        dueDate: DateTime.now().plus({ day: this.client?.payment_term_days || 0 }).toISODate(),
      };
    },
    client() {
      return this.supplyOrder.client;
    },
    location() {
      return this.supplyOrder.location;
    },
    vendor() {
      return this.supplyOrder.vendor;
    },
    supplyOrderItemsColumns() {
      return this.$store.getters.columnsForTable(this.supplyOrderItemsTable);
    },
    margin() {
      const amount = this.supplyOrder.price.amount - this.supplyOrder.cost.amount;
      return amount < 0 ? 0 : amount;
    },
    marginPercentage() {
      return this.margin === 0 && this.supplyOrder.price.amount === 0 ? '' : this.$options.filters.percentFormatFilter(this.margin / this.supplyOrder.price.amount);
    },
    documentsColumns() {
      return this.$store.getters.columnsForTable(this.documentsTableName);
    },
    toggleActiveTooltipText() {
      return !this.supplyOrder.deleted_at ? 'Cancel' : 'Uncancel';
    },
    toggleActiveConfirmText() {
      return !this.supplyOrder.deleted_at
        ? 'Are you sure you want to cancel the Order?'
        : 'Are you sure you want to uncancel the Order?';
    },
    isCMSOrganization() {
      return this.$store.getters.currentOrganization?.id === 1;
    }
  },
  methods: {
    updateOrder(supplyOrder) {
      this.supplyOrder = supplyOrder;
      this.refreshTable();
      this.supplyOrderKey = `${Date.now()}`;
    },
    getSupplyOrder() {
      this.$supplyOrdersAPI
        .get(this.$route.params.id)
        .then((supplyOrder) => (this.supplyOrder = supplyOrder))
        .catch(() => {
          this.$router.push('/404');
        });
    },
    getInvoices() {
      this.$invoicesAPI.getInvoices({ supply_order_id: this.$route.params.id }).then((response) => {
        this.invoices = response.invoices;
      });
    },
    toggleActive() {
      this.$swal({
        text: this.toggleActiveConfirmText,
        preConfirm: () => this.$supplyOrdersAPI.update(this.$route.params.id, { deleted_at: (!this.supplyOrder.deleted_at ? DateTime.now().toISODate() : null) }),
      }).then((result) => {
        if (result.value) {
          this.supplyOrder = result.value;
        }
        result.timer;
      });
    },
    refreshTable() {
      this.tableKey = Date.now();
    },
    refreshDocuments() {
      this.documentsKey = `documents-${Date.now()}`;
    },
    applyFilters({ filters }) {
      this.filters = Object.assign({}, filters);
    },
  },
};
</script>
